import React, { useEffect, useMemo } from "react"
import { useAppDispatch } from "../../../store/hooks"
import styles from "./DialogOperatorFeedbackForm.module.scss"
import cn from "classnames"
import { DIALOG_OPERATION_FEEDBACK_FORM_CONST } from "./DialogOperatorFeedbackForm.const"
import { useTranslation } from "react-i18next"
import { formTranslation } from "../../../locales/form"
import LoadingButton from "../../LoadingButton/LoadingButton"
import { OperatorFeedbackValues } from "../../../models/Dialogs/operatorFeedbackForm"
import { Form, Formik } from "formik"
import { useSendOperatorFeedbackMutation } from "../../../api/controllers/dialogs"
import { OperatorFeedbackRequest } from "../../../models/Dialogs/dialog"
import FeedbackFormHeader from "./components/FeedbackFormHeader/FeedbackFormHeader"
import FeedbackFormContent from "./components/FeedbackFormContent/FeedbackFormContent"
import FeedbackFormSuccess from "./components/FeedbackFormSuccess/FeedbackFormSuccess"
import FeedbackFormErrorView from "./components/FeedbackFormErrorView/FeedbackFormErrorView"
import { getValidationSchema } from "./DialogOperatorFeedbackForm.schema"
import { actions } from "../../../store/dialogs/slice"

const { tNamespace, tPaths, testIds } = DIALOG_OPERATION_FEEDBACK_FORM_CONST

const DialogOperatorFeedbackForm = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation(tNamespace)

    const validationSchema = useMemo(() => getValidationSchema(t), [t])

    const [sendOperatorFeedback, status] = useSendOperatorFeedbackMutation()
    const { isUninitialized, isSuccess, isError, isLoading } = status

    useEffect(() => {
        if (!isSuccess) {
            return
        }

        // Закрываем окно спустя 5 секунд, если пользователь не закрыл ее сам
        const timer = setTimeout(() => {
            dispatch(actions.setOperatorFeedbackFormState(false))
        }, 5000)

        return () => clearTimeout(timer)
    }, [isSuccess, dispatch])

    const handleSubmit = (values: OperatorFeedbackValues) => {
        const request: OperatorFeedbackRequest = {
            ...values
        }

        sendOperatorFeedback(request)
    }

    const retrySubmit = (e: React.MouseEvent<HTMLButtonElement>, values: OperatorFeedbackValues) => {
        e.preventDefault()

        handleSubmit(values)
    }

    const getHeaderText = () => {
        if (isError) {
            return t(tPaths.errorHeader)
        }

        if (isSuccess) {
            return t(tPaths.successHeader)
        }

        return t(tPaths.defaultHeader)
    }

    return (
        <Formik<OperatorFeedbackValues>
            initialValues={validationSchema.default()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {formikProps => {
                const { isValid, values } = formikProps

                return (
                    <Form>
                        <div className={styles.form}>
                            <FeedbackFormHeader text={getHeaderText()} isError={isError} />

                            <div className={styles.form__main}>
                                {isError && <FeedbackFormErrorView onRetrySubmit={e => retrySubmit(e, values)} />}
                                {isSuccess && <FeedbackFormSuccess />}
                                {isUninitialized && <FeedbackFormContent />}
                            </div>

                            {isUninitialized && (
                                <LoadingButton
                                    type="submit"
                                    loading={isLoading}
                                    variant="primary"
                                    disabled={!isValid}
                                    block
                                    testId={testIds.submitButton}
                                    className={cn(
                                        styles.form__submitButton,
                                        !isValid && styles.form__submitButton_disabled
                                    )}
                                >
                                    {t(formTranslation.send)}
                                </LoadingButton>
                            )}
                        </div>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default DialogOperatorFeedbackForm
